/**
 * RiskFlagBadges — Renders RISK_* flag codes as amber warning badges.
 *
 * Labels are sourced from `PRE_RISK_FLAG_LABELS` in `@respectly/shared-types`.
 * That map is typed `Record<PRERiskFlag, string>` so adding a new risk flag
 * without a label is a TypeScript compile error — no Decision-View component
 * is allowed to maintain its own label subset.
 */
import { Badge } from '@respectly/ui';
import { AlertTriangle } from 'lucide-react';
import { PRE_RISK_FLAG_LABELS, type PRERiskFlag } from '@respectly/shared-types';

interface RiskFlag {
  code: string;
  description?: string;
}

interface RiskFlagBadgesProps {
  flags: RiskFlag[];
  className?: string;
}

export function RiskFlagBadges({ flags, className }: RiskFlagBadgesProps) {
  if (!flags.length) return null;

  return (
    <div className={className}>
      <div className="flex flex-wrap gap-1.5">
        {flags.map((flag) => (
          <Badge
            key={flag.code}
            variant="warning"
            title={flag.description ?? flag.code}
          >
            <AlertTriangle className="w-3 h-3 mr-1" />
            {PRE_RISK_FLAG_LABELS[flag.code as PRERiskFlag] ?? flag.code}
          </Badge>
        ))}
      </div>
    </div>
  );
}
